<template>
  <div class="kq-theme-container">
    <!--顶栏-->
    <div class="kq-theme-header">
      <!--一级菜单LOGO-->
      <router-link :to="menuLogoPath" class="kq-theme-menu">
        <img src="../assets/image/icon-logo-white.png" alt="" />
      </router-link>
      <!--二级菜单LOGO-->
      <router-link
        v-if="submenus && submenus.length > 0"
        :to="submenuLogoPath"
        :class="{ 'kq-theme-submenu': true, 'kq-theme-submenu-collapse': collapse }"
      >
        <img src="../assets/image/icon-logo.png" alt="" />
        <span v-if="!collapseSubmenu" class="kq-theme-submenu-title">{{ submenuTitle }}</span>
      </router-link>
      <!--折叠二级菜单按钮-->
      <div @click="$store.dispatch('menu/toggle')">
        <kq-icon class="kq-theme-header-button" name="bars" />
      </div>
      <!--当前页面路径-->
      <el-breadcrumb class="kq-theme-breadcrumb" separator="/">
        <el-breadcrumb-item v-for="(route, index) of breadcrumbList" :key="index" :to="{ path: route.path }">
          {{ route.meta.title }}
        </el-breadcrumb-item>
      </el-breadcrumb>
      <!--搜索路由按钮-->
      <div @click="isSearch = !isSearch">
        <kq-icon class="kq-theme-header-button" name="search" />
      </div>
      <!--刷新当前页面按钮-->
      <div @click="refreshCurrentPage">
        <kq-icon class="kq-theme-header-button" name="refresh" />
      </div>
      <!--全屏切换按钮按钮-->
      <div @click="$store.dispatch('fullscreen/toggle')">
        <kq-icon class="kq-theme-header-button" :name="active ? 'compress' : 'arrows-alt'" />
      </div>
      <!--用户信息-->
      <user-info />
    </div>
    <!-- 主体 -->
    <div class="kq-theme-content">
      <!-- 一级菜单 -->
      <div class="kq-theme-menu-container">
        <side-menu :menus="menus" :current-menu-path="menuPath" />
      </div>
      <!-- 二级菜单 -->
      <div
        v-if="submenus && submenus.length > 0"
        :class="{ 'kq-theme-submenu-container': true, 'kq-theme-submenu-collapse': collapse }"
      >
        <side-submenu :menus="submenus" :collapse="collapseSubmenu" :current-submenu-path="submenuPath" />
      </div>
      <div class="kq-theme-main-container">
        <!-- 搜索路由 -->
        <transition name="fade-scale">
          <div v-if="isSearch" class="absolute-full flex-column">
            <search-menu @back="isSearch = false" />
          </div>
        </transition>
        <!-- 主体 -->
        <div class="absolute-full flex-column" :style="{ opacity: isSearch ? 0 : 1, transition: 'opacity 0.3s' }">
          <el-alert
            v-if="accountExpired < 3"
            :title="
              `${info.effectiveTime ? '账号有效期至：' + info.effectiveTime : ''}您的企业账号${
                accountExpired < 0 ? '已过期' : '即将过期'
              }，如需继续使用，请联系在线客服续费`
            "
            :type="accountExpired < 0 ? 'error' : 'warning'"
          />
          <div class="kq-theme-tab-container">
            <page-tabs />
          </div>
          <!-- 主体 -->
          <div class="kq-theme-router-container">
            <transition name="fade-transverse" mode="out-in">
              <keep-alive :max="20">
                <router-view v-if="isKeepAlive" />
              </keep-alive>
            </transition>
            <transition name="fade-transverse" mode="out-in">
              <router-view v-if="!isKeepAlive" />
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserInfo from './components/UserInfo'
import SideMenu from './components/SideMenu'
import SideSubmenu from './components/SideSubmenu'
import PageTabs from './components/PageTabs'
import SearchMenu from './components/SearchMenu'
import { mapState } from 'vuex'
import setting from '@/setting'
import { getVersionInfo } from '@/api/common/version'
import { useCookiesUtil } from '@/utils/cookies'

export default {
  name: 'AxLayoutHeaderAside',
  components: {
    PageTabs,
    UserInfo,
    SideMenu,
    SideSubmenu,
    SearchMenu
  },
  data() {
    return {
      collapseSubmenu: false,
      breadcrumbList: [],
      menuPath: '',
      submenuPath: '',
      isSearch: false,
      isKeepAlive: false
    }
  },
  computed: {
    ...mapState('menu', ['menus', 'submenus', 'collapse']),

    ...mapState('fullscreen', ['active']),

    ...mapState('tab', ['tabs']),

    ...mapState('user', ['info']),

    menuLogoPath() {
      return this.menus[0].path
    },

    submenuLogoPath() {
      const routes = this.$route.path.split('/')
      return `${routes[0]}/${routes[1]}`
    },

    submenuTitle() {
      const menu = this.menus.find(menu => menu.path === this.submenuLogoPath)
      return (menu ? menu.title : '') || process.env.VUE_APP_TITLE
    },

    accountExpired() {
      if (!this.info || !this.info.effectiveTime || !this.info.currentTime) {
        return -1
      }
      const effectiveTime = this.$dayjs(this.info.effectiveTime)
      const currentTime = this.$dayjs(this.info.currentTime)
      return effectiveTime.diff(currentTime, 'day')
    }
  },
  watch: {
    collapse(val) {
      if (val) {
        this.collapseSubmenu = true
      } else {
        setTimeout(() => (this.collapseSubmenu = false), 300)
      }
    },
    '$route.matched': {
      handler(routes) {
        // 面包屑
        this.breadcrumbList = (routes || []).filter(route => !!route.meta.title)
        // 一级菜单选中
        this.menuPath = routes[0].path || '/'
        // 二级菜单选中
        const route = routes[routes.length - 1]
        this.submenuPath = route.path
        // 是否使用keep-alive
        const isDisableTabCache = route.meta.disableTabCache
        const isTabCache = setting.tabCache || route.meta.tabCache
        this.isKeepAlive = !isDisableTabCache && isTabCache && this.tabs.some(tab => route.path === tab.path)
        // 二级菜单
        const menu = this.menus.find(menu => menu.path === this.menuPath && menu.path !== '/')
        this.$store.commit('menu/setSubmenu', menu ? menu.children || [] : [])
        // tab
        this.$store.dispatch('tab/open', route)
      },
      immediate: true
    }
  },
  mounted() {
    this.collapseSubmenu = this.collapse
    this.getVersionInfo()
    this.cancelWheel()
  },
  methods: {
    /**
     * 刷新当前页面
     */
    refreshCurrentPage() {
      this.$router.go(0)
    },

    /**
     * 获取版本信息
     */
    async getVersionInfo() {
      try {
        const res = await getVersionInfo()
        if (res && res !== useCookiesUtil.get('version') && Number(res) > Number(setting.version)) {
          await this.$confirm('检测到新版本，请及时更新', '版本提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          })
          useCookiesUtil.set('version', res)
          window.location.reload()
        }
      } catch (error) {
        console.log(error)
      }
    },

    /**
     * 取消滚动事件
     */
    cancelWheel() {
      document.addEventListener(
        'wheel',
        function() {
          const activeEl = document.activeElement
          if (activeEl && activeEl.type === 'number') {
            activeEl.blur()
          }
        },
        { passive: true }
      )
    }
  }
}
</script>
